import {ElementCursor, ElementProperties, ElementView} from '@app/modules/svg/element/ElementView';
import {MeshView, StyledEdge} from '@app/modules/svg/element/shape/pointed/MeshView';
import {Container} from '@app/modules/svg/Container';
import {Rect} from '@app/modules/svg/model/Rect';
import {MoveDrawable} from '@app/modules/svg/service/tool/draw/type/MoveDrawable';
import {ElementType} from '@app/modules/svg/dataSource/constant/ElementType';
import {LineTo} from '@app/modules/svg/model/path/line/LineTo';
import {Style} from '@app/modules/svg/service/style/Style';
import {Arc} from '@app/modules/svg/model/path/curve/arc/Arc';
import {Point} from '@app/modules/svg/model/Point';
import {lineMidPoint} from '@app/modules/svg/service/math/Line';

const ARC_X_RADIUS_COEFFICIENT: number = 1 / 2;
const ARC_Y_RADIUS_COEFFICIENT: number = 1 / 6;

export class CylinderCursor extends ElementCursor {}

export interface CylinderProperties extends ElementProperties {}

export class CylinderView extends MeshView implements MoveDrawable {
  protected _type: ElementType = ElementType.CYLINDER;
  protected static readonly CENTER_POINT_RADIUS: number = 3;
  protected svgTopCenterElement: SVGCircleElement = document.createElementNS(ElementView.svgURI, 'circle');
  protected svgBottomCenterElement: SVGCircleElement = document.createElementNS(ElementView.svgURI, 'circle');

  public constructor(container: Container,
                     properties: CylinderProperties = {},
                     rect: Rect = {x: 0, y: 0, width: 1, height: 1},
                     ownerId?: string, index?: number) {

    const cylinder = CylinderView.rectToCylinderMesh(rect);
    super(container, properties, cylinder.points, cylinder.edges, ownerId, index);
    this.svgElement.id = this.id;

    this._rect = ElementView.calculateRect(this.points);
    this.__fixRect__();

    this.setProperties(properties);

    this.setAttr({
      'stroke-width': 0,
      fill: '#000',
      r: CylinderView.CENTER_POINT_RADIUS
    }, this.svgTopCenterElement);

    this.setAttr({
      'stroke-width': 0,
      fill: '#000',
      r: CylinderView.CENTER_POINT_RADIUS
    }, this.svgBottomCenterElement);
  }

  public __drawSize__(rect: Rect): void {
    const cylinder = CylinderView.rectToCylinderMesh(rect);
    this._points = cylinder.points;
    this._styledEdges = cylinder.edges;

    this._rect = ElementView.calculateRect(this.points);

    this.__updateView__();
  }

  public __updateView__() {
    super.__updateView__();

    const topCenter: Point = lineMidPoint({
      p0: this._points[0],
      p1: this._points[2],
    });

    this.setAttr({
      cx: topCenter.x,
      cy: topCenter.y
    }, this.svgTopCenterElement);

    if (this.svgTopCenterElement) {
      this.svgElement.appendChild(this.svgTopCenterElement);
    }

    const bottomCenter: Point = lineMidPoint({
      p0: this._points[4],
      p1: this._points[6],
    });

    this.setAttr({
      cx: bottomCenter.x,
      cy: bottomCenter.y
    }, this.svgBottomCenterElement);

    if (this.svgBottomCenterElement) {
      this.svgElement.appendChild(this.svgBottomCenterElement);
    }
  }

  public override replacePoint(index: number, point: Point): void {
    if (index < 0) {
      index = this.points.length + index;
    }

    this._points[index] = point;

    for (const edge of this._styledEdges) {
      if ((edge.point2 === index || edge.point1 === index) && edge.command instanceof Arc) {
        edge.command.rx = Math.abs(this._points[edge.point1].x - this._points[edge.point2].x);
        edge.command.ry = Math.abs(this._points[edge.point1].y - this._points[edge.point2].y);
      }
    }

    this._rect = ElementView.calculateRect(this.points);
    this.__updateView__();
  };

  private static rectToCylinderMesh(rect: Rect): { points: Point[]; edges: StyledEdge[] } {
    let sweepFlag: 1 | 0 = 1;
    if (rect.width < 0 !== rect.height < 0) {
      sweepFlag = 0;
    }

    const arcXRadius: number = rect.width * ARC_X_RADIUS_COEFFICIENT;
    const arcYRadius: number = rect.height * ARC_Y_RADIUS_COEFFICIENT;

    const dashLineStyle: Style = new Style();
    dashLineStyle.strokeDashArray = '4 4';

    const points: Point[] = [
      {x: rect.x, y: rect.y + arcYRadius},
      {x: rect.x + rect.width / 2, y: rect.y},
      {x: rect.x + rect.width, y: rect.y + arcYRadius},
      {x: rect.x + rect.width / 2, y: rect.y + arcYRadius * 2},

      {x: rect.x, y: rect.y + rect.height - arcYRadius},
      {x: rect.x + rect.width / 2, y: rect.y + rect.height - arcYRadius * 2},
      {x: rect.x + rect.width, y: rect.y + rect.height - arcYRadius},
      {x: rect.x + rect.width / 2, y: rect.y + rect.height},
    ];

    return {
      points,
      edges: [
        /* top face */
        {point1: 0, point2: 1, command: new Arc(arcXRadius, arcYRadius, 0, 0, sweepFlag, {x: 0, y: 0})},
        {point1: 1, point2: 2, command: new Arc(arcXRadius, arcYRadius, 0, 0, sweepFlag, {x: 0, y: 0})},
        {point1: 2, point2: 3, command: new Arc(arcXRadius, arcYRadius, 0, 0, sweepFlag, {x: 0, y: 0})},
        {point1: 3, point2: 0, command: new Arc(arcXRadius, arcYRadius, 0, 0, sweepFlag, {x: 0, y: 0})},

        /* bottom face */
        {point1: 4, point2: 5, command: new Arc(arcXRadius, arcYRadius, 0, 0, sweepFlag, {x: 0, y: 0}), style: dashLineStyle},
        {point1: 5, point2: 6, command: new Arc(arcXRadius, arcYRadius, 0, 0, sweepFlag, {x: 0, y: 0}), style: dashLineStyle},
        {point1: 6, point2: 7, command: new Arc(arcXRadius, arcYRadius, 0, 0, sweepFlag, {x: 0, y: 0})},
        {point1: 7, point2: 4, command: new Arc(arcXRadius, arcYRadius, 0, 0, sweepFlag, {x: 0, y: 0})},

        /* left edge */
        {point1: 0, point2: 4, command: new LineTo({x: 0, y: 0})},
        /* right edge */
        {point1: 2, point2: 6, command: new LineTo({x: 0, y: 0})},
      ]
    };
  }
}
